import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Flex, Form } from "antd"
import { useTranslation } from "react-i18next"
import Counter from "ui/Counter"
import { fitGuestCountsToTotal, getFirstAllowedGuestType, sumGuestCounts } from "../helpers/guestTotals"
import styles from "./BookingForm.module.css"

// The counter inside the Form.Item, which passes it the stored total and stores each change before
// calling the field's own onChange.
const TotalCounter = ({ value, onChange, min, max }) => {
  const { t } = useTranslation()
  const count = Number(value) || min
  return (
    <Counter
      value={count}
      decreaseLabel={t("booking.total_guests.fewer")}
      increaseLabel={t("booking.total_guests.more")}
      isDecreaseDisabled={count <= min}
      isIncreaseDisabled={max > 0 && count >= max}
      onChange={onChange}
    />
  )
}

TotalCounter.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func,
  min: PropTypes.number.isRequired,
  max: PropTypes.number
}

TotalCounter.defaultProps = {
  value: undefined,
  onChange: undefined,
  max: 0
}

// New Booking's "Total Guests" (redesign, Figma 7385:153215). Per the designer's note it starts at
// 1 (or the experience's minimum) and follows the guest counts once they are set; changing it refits
// those counts. It is meant to grey out dates without enough places, which needs the availability
// API to take a guest count (guide-orders-backend.md, G7) — until then it only sets the guests.
const TotalGuestsField = ({ form, minGuests, maxGuests, tripPrice, onGuestsChange }) => {
  const { t } = useTranslation()
  const minTotal = Math.max(1, Number(minGuests) || 0)
  const mainCounts = Form.useWatch("main", form)
  const total = Form.useWatch("totalGuests", form)
  const guestSum = sumGuestCounts(mainCounts)

  // Follow the guest box once it has counts (and start over at the minimum for a new experience).
  useEffect(() => {
    if (guestSum > 0 && guestSum !== total) form.setFieldValue("totalGuests", guestSum)
    else if (guestSum === 0 && total == null) form.setFieldValue("totalGuests", minTotal)
  }, [guestSum, total, minTotal, form])

  const handleTotalChange = (nextTotal) => {
    if (guestSum === 0) return
    const fitted = fitGuestCountsToTotal(mainCounts, nextTotal, getFirstAllowedGuestType(tripPrice))
    form.setFieldsValue({ main: fitted })
    onGuestsChange(fitted)
    form.validateFields(["guestTotal"]).catch(() => {})
  }

  return (
    <Flex align="center" justify="space-between" gap="var(--spacing-s)" className={styles.totalGuestsBox}>
      <Flex vertical>
        <span className={styles.totalGuestsTitle}>{t("booking.total_guests.title")}</span>
        {maxGuests > 0 && <span className={styles.totalGuestsLimit}>{t("booking.total_guests.limit", { max: maxGuests })}</span>}
      </Flex>
      <Form.Item name="totalGuests" initialValue={minTotal} noStyle>
        <TotalCounter min={minTotal} max={maxGuests} onChange={handleTotalChange} />
      </Form.Item>
    </Flex>
  )
}

TotalGuestsField.propTypes = {
  form: PropTypes.object.isRequired,
  minGuests: PropTypes.number,
  maxGuests: PropTypes.number,
  tripPrice: PropTypes.object,
  onGuestsChange: PropTypes.func.isRequired
}

TotalGuestsField.defaultProps = {
  minGuests: 0,
  maxGuests: 0,
  tripPrice: null
}

export default TotalGuestsField
